import { useEffect, useState } from 'react';
import { GameStatus, type GameDTO } from '@pickem/shared';
import { Card } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { api, ApiError } from '../../lib/api';
import { isoToLocalInput, localInputToIso, formatKickoff } from '../../lib/format';

const STATUS_OPTIONS: GameStatus[] = [
  GameStatus.SCHEDULED,
  GameStatus.IN_PROGRESS,
  GameStatus.FINAL,
  GameStatus.POSTPONED,
  GameStatus.CANCELED,
];

/** Editable draft for one game row. Scores/kickoff are kept as input strings. */
interface Draft {
  status: GameStatus;
  homeScore: string;
  awayScore: string;
  eligible: boolean;
  kickoff: string;
}

function toDraft(g: GameDTO): Draft {
  return {
    status: g.status,
    homeScore: g.homeScore != null ? String(g.homeScore) : '',
    awayScore: g.awayScore != null ? String(g.awayScore) : '',
    eligible: g.eligible,
    kickoff: isoToLocalInput(g.kickoff),
  };
}

function draftsFor(games: GameDTO[]): Record<string, Draft> {
  return Object.fromEntries(games.map((g) => [g.id, toDraft(g)]));
}

/** Fields where the draft differs from the saved game, as an API patch. */
function buildPatch(game: GameDTO, d: Draft) {
  const base = toDraft(game);
  const patch: {
    status?: GameStatus;
    homeScore?: number | null;
    awayScore?: number | null;
    eligible?: boolean;
    kickoff?: string;
  } = {};
  if (d.status !== base.status) patch.status = d.status;
  if (d.homeScore !== base.homeScore) patch.homeScore = d.homeScore === '' ? null : Number(d.homeScore);
  if (d.awayScore !== base.awayScore) patch.awayScore = d.awayScore === '' ? null : Number(d.awayScore);
  if (d.eligible !== base.eligible) patch.eligible = d.eligible;
  if (d.kickoff !== base.kickoff) {
    const iso = localInputToIso(d.kickoff);
    if (iso) patch.kickoff = iso;
  }
  return patch;
}

function isDirty(game: GameDTO, d: Draft): boolean {
  return Object.keys(buildPatch(game, d)).length > 0;
}

export function GamesPanel({
  weekId,
  games,
  onChanged,
}: {
  weekId: string;
  games: GameDTO[];
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const canEditDetails = !!user?.isSuperAdmin;

  const [drafts, setDrafts] = useState<Record<string, Draft>>(() => draftsFor(games));
  const [savingAll, setSavingAll] = useState(false);
  const [saveAllErr, setSaveAllErr] = useState('');

  // Reset drafts whenever the saved games change (initial load, or a reload
  // after a save/sync). Unsaved edits are intentionally discarded on reload.
  useEffect(() => {
    setDrafts(draftsFor(games));
  }, [games]);

  const dirtyCount = games.filter((g) => drafts[g.id] && isDirty(g, drafts[g.id])).length;

  async function saveAll() {
    const changed = games
      .filter((g) => drafts[g.id] && isDirty(g, drafts[g.id]))
      .map((g) => ({ id: g.id, ...buildPatch(g, drafts[g.id]) }));
    if (changed.length === 0) return;
    setSavingAll(true);
    setSaveAllErr('');
    try {
      await api.patch(`/admin/weeks/${weekId}/games`, { games: changed });
      onChanged();
    } catch (e) {
      setSaveAllErr(e instanceof ApiError ? e.message : 'Unable to save all changes.');
    } finally {
      setSavingAll(false);
    }
  }

  return (
    <Card>
      <h3 className="mb-1 font-display text-2xl uppercase text-ink">Games</h3>
      <p className="mb-3 text-xs text-muted">
        Choose which games count for picks and the Sunday Night tiebreaker.
        {canEditDetails
          ? ' Editing a time, status, or score locks it from provider sync.'
          : ' Game times, statuses, and scores are managed by a super-admin.'}
      </p>
      {games.length === 0 ? (
        <p className="text-sm text-muted">No games yet. Use “Sync NFL schedule” above.</p>
      ) : (
        <>
          <div className="space-y-2">
            {games.map((g) =>
              drafts[g.id] ? (
                <GameRow
                  key={g.id}
                  weekId={weekId}
                  game={g}
                  draft={drafts[g.id]}
                  canEditDetails={canEditDetails}
                  dirty={isDirty(g, drafts[g.id])}
                  onChange={(partial) =>
                    setDrafts((prev) => ({ ...prev, [g.id]: { ...prev[g.id], ...partial } }))
                  }
                  onChanged={onChanged}
                />
              ) : null,
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-3">
            <button className="btn-primary" onClick={saveAll} disabled={savingAll || dirtyCount === 0}>
              {savingAll ? 'Saving…' : dirtyCount > 0 ? `Save all (${dirtyCount})` : 'Save all'}
            </button>
            {dirtyCount > 0 && !savingAll && (
              <span className="text-xs text-muted">
                {dirtyCount} game{dirtyCount === 1 ? '' : 's'} with unsaved changes
              </span>
            )}
            {saveAllErr && <span className="text-sm text-danger">{saveAllErr}</span>}
          </div>
        </>
      )}
    </Card>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-xs">
      <span className="mb-1 block text-muted">{label}</span>
      <span className="block text-sm text-ink">{value}</span>
    </div>
  );
}

function GameRow({
  weekId,
  game,
  draft,
  canEditDetails,
  dirty,
  onChange,
  onChanged,
}: {
  weekId: string;
  game: GameDTO;
  draft: Draft;
  canEditDetails: boolean;
  dirty: boolean;
  onChange: (partial: Partial<Draft>) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Submissions close 5 minutes before the first eligible game, so a game that
  // is already within 5 minutes of kickoff can't be newly marked eligible (it
  // would put the close time in the past). Turning an eligible game OFF is fine.
  const withinLockWindow = new Date(game.kickoff).getTime() - Date.now() <= 5 * 60 * 1000;
  const eligibleLocked = withinLockWindow && !game.eligible;

  async function save() {
    const patch = buildPatch(game, draft);
    if (Object.keys(patch).length === 0) return;
    setBusy(true);
    setError('');
    try {
      await api.patch(`/admin/games/${game.id}`, patch);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Unable to save.');
    } finally {
      setBusy(false);
    }
  }

  async function designateTiebreaker() {
    setBusy(true);
    setError('');
    try {
      await api.post(`/admin/weeks/${weekId}/tiebreaker`, { gameId: game.id });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Unable to set tiebreaker.');
    } finally {
      setBusy(false);
    }
  }

  const scoreText = (n: number | null) => (n != null ? String(n) : '—');

  return (
    <div className="rounded border border-line p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium">
          {game.awayTeam.abbreviation} @ {game.homeTeam.abbreviation}
          {game.isTiebreaker && <span className="ml-2 chip bg-amber/20 text-amber-dark">SNF</span>}
          {!game.eligible && <span className="ml-2 chip bg-line text-muted">Not eligible</span>}
        </div>
        <div className="text-xs text-muted">{formatKickoff(game.kickoff)}</div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-6">
        {canEditDetails ? (
          <label className="col-span-2 text-xs sm:col-span-2">
            <span className="mb-1 block text-muted">Kickoff (your local time)</span>
            <input
              type="datetime-local"
              className="input"
              value={draft.kickoff}
              onChange={(e) => onChange({ kickoff: e.target.value })}
            />
          </label>
        ) : (
          <div className="col-span-2 sm:col-span-2">
            <ReadOnlyField label="Kickoff" value={formatKickoff(game.kickoff)} />
          </div>
        )}

        {canEditDetails ? (
          <label className="text-xs">
            <span className="mb-1 block text-muted">Status</span>
            <select
              className="input"
              value={draft.status}
              onChange={(e) => onChange({ status: e.target.value as GameStatus })}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <ReadOnlyField label="Status" value={game.status} />
        )}

        {canEditDetails ? (
          <label className="text-xs">
            <span className="mb-1 block text-muted">{game.awayTeam.abbreviation} (away)</span>
            <input
              type="number"
              min={0}
              className="input"
              value={draft.awayScore}
              onChange={(e) => onChange({ awayScore: e.target.value })}
            />
          </label>
        ) : (
          <ReadOnlyField label={`${game.awayTeam.abbreviation} (away)`} value={scoreText(game.awayScore)} />
        )}

        {canEditDetails ? (
          <label className="text-xs">
            <span className="mb-1 block text-muted">{game.homeTeam.abbreviation} (home)</span>
            <input
              type="number"
              min={0}
              className="input"
              value={draft.homeScore}
              onChange={(e) => onChange({ homeScore: e.target.value })}
            />
          </label>
        ) : (
          <ReadOnlyField label={`${game.homeTeam.abbreviation} (home)`} value={scoreText(game.homeScore)} />
        )}

        <label className="flex items-end gap-2 text-xs">
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={draft.eligible}
            disabled={eligibleLocked}
            onChange={(e) => onChange({ eligible: e.target.checked })}
          />
          <span className="text-muted">
            Eligible for picks
            {eligibleLocked && (
              <span className="mt-0.5 block text-[10px] text-muted">Too close to kickoff to add</span>
            )}
          </span>
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button className="btn-primary" onClick={save} disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {!game.isTiebreaker && (
          <button className="btn-outline" onClick={designateTiebreaker} disabled={busy}>
            Set as SNF tiebreaker
          </button>
        )}
        {error && <span className="text-sm text-danger">{error}</span>}
      </div>
    </div>
  );
}
